import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Skeleton, Spin } from 'antd';
import { StyledMachineListPanel } from './MachineListPanel.styles';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { DrawersActions } from 'app/cross-cutting-concerns/drawers/state/drawersSlice';
import { MachinesReport } from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import {
  getDashboardMachineDuration,
  getDashboardMachineLastConnection,
  getDashboardMachineManager,
  getDashboardMachineName,
  getDashboardMachineSite,
} from 'app/modules/cleaning/widgets/machine-dashboard/utils/machineDashboardWidgetListUtils';

interface MachineListPanelProps {
  title: string;
  machines: MachinesReport[];
  cardId?: string;
  emptyDescription?: string;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  isEmpty?: boolean;
}

export const MachineListPanel = ({
  title,
  machines,
  cardId,
  emptyDescription,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  isEmpty,
}: MachineListPanelProps): React.JSX.Element => {
  const isGeofencing = cardId === 'geofencing';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const checkAtBottom = useCallback((el: HTMLDivElement): boolean => {
    if (el.scrollHeight <= el.clientHeight) {
      return true;
    }

    return el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  }, []);

  const shouldLoadMore = useCallback((el: HTMLDivElement): boolean => {
    if (el.scrollHeight <= el.clientHeight) {
      return false;
    }

    return el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    setIsAtBottom(checkAtBottom(el));

    if (shouldLoadMore(el) && hasMore && !isLoadingMore && !isLoading) {
      onLoadMore();
    }
  }, [checkAtBottom, shouldLoadMore, hasMore, isLoading, isLoadingMore, onLoadMore]);

  useEffect(() => {
    if (isLoading) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;

    const check = (): void => setIsAtBottom(checkAtBottom(el));
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLoading, machines.length, checkAtBottom]);

  if (!isLoading && isEmpty) {
    return (
      <StyledMachineListPanel className="machine-list-panel">
        <p className="machine-list-panel__title">{t(title)}</p>
        <div className="machine-list-panel__empty">
          <span aria-hidden="true">
            <SvgIcon name="circleCheck" className="machine-list-panel__empty-icon" />
          </span>
          <span className="machine-list-panel__empty-description">
            {emptyDescription ? t(emptyDescription) : t('common.noData')}
          </span>
        </div>
      </StyledMachineListPanel>
    );
  }

  if (isLoading) {
    return (
      <StyledMachineListPanel className="machine-list-panel">
        <p className="machine-list-panel__title">{t(title)}</p>
        <div className="machine-list-panel__skeleton">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="machine-list-panel__skeleton-item">
              <Skeleton active title paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      </StyledMachineListPanel>
    );
  }

  return (
    <StyledMachineListPanel className="machine-list-panel">
      <p className="machine-list-panel__title">{t(title)}</p>
      <div
        className={`machine-list-panel__scroll-wrapper${
          isAtBottom ? ' machine-list-panel__scroll-wrapper--at-bottom' : ''
        }`}
      >
        <div className="machine-list-panel__scroll" ref={scrollRef} onScroll={handleScroll}>
          {machines.map(machine => {
            const machineName = getDashboardMachineName(machine);

            return (
              <div key={machine.machineId} className="machine-list-panel__item">
                <div className="machine-list-panel__item-content">
                  <div className="machine-list-panel__item-name-row">
                    <p className="machine-list-panel__item-name">{machineName}</p>
                    <KccButton
                      type="link"
                      aria-label={machineName}
                      className="machine-list-panel__item-arrow"
                      icon={<SvgIcon name="externalLink" />}
                      onClick={(): void => {
                        dispatch(DrawersActions.showMachineDetailsDrawer({ machineId: machine.machineId }));
                      }}
                    />
                  </div>
                  <div className="machine-list-panel__item-meta">
                    {isGeofencing ? (
                      <span className="machine-list-panel__item-meta-row">
                        <SvgIcon name="locationMap" />
                        <span className="machine-list-panel__item-meta-text">{getDashboardMachineSite(machine)}</span>
                      </span>
                    ) : (
                      <span className="machine-list-panel__item-meta-row">
                        <SvgIcon name="duration" />
                        <span className="machine-list-panel__item-meta-text">
                          {getDashboardMachineDuration(machine)}
                        </span>
                      </span>
                    )}
                    <span className="machine-list-panel__item-meta-row">
                      <SvgIcon name="calendar" />
                      <span className="machine-list-panel__item-meta-text">
                        {getDashboardMachineLastConnection(machine)}
                      </span>
                    </span>
                    {machine.address && (
                      <span className="machine-list-panel__item-meta-row">
                        <SvgIcon name="locationPin" />
                        <span className="machine-list-panel__item-meta-text">{machine.address}</span>
                      </span>
                    )}
                    <span className="machine-list-panel__item-meta-row">
                      <SvgIcon name="userManagement" />
                      <span className="machine-list-panel__item-meta-text">{getDashboardMachineManager(machine)}</span>
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {isLoadingMore && (
        <div className="machine-list-panel__loader">
          <Spin size="small" />
        </div>
      )}
    </StyledMachineListPanel>
  );
};
